import type {
  Camp,
  InputVector,
  MatchResult,
  MatchState,
  SimulationEvent,
  TickInputs,
} from '@orbomb/sim';
import { SIMULATION_VERSION } from '@orbomb/sim';

/** Competitive messages live here, not in `@orbomb/sim`. */
export const PROTOCOL_VERSION = 1;
export const PROTOCOL_STATUS = 'colyseus-authoritative';

/** Colyseus room type. Filter matchmaking by `supabaseRoomId`. */
export const ROOM_NAME = 'orbomb';

/**
 * Seconds the server keeps a dropped seat (architecture §30 / spec §20.7).
 * Client Colyseus reconnect budget must stay within this window (ADR-026).
 */
export const MATCH_RECONNECT_SECONDS = 10;

export const TICK_RATE = 60;

/**
 * Snapshot at 20 Hz (architecture §22). The client predicts the local ball
 * between snapshots; remotes interpolate. Full-state JSON at 60 Hz GC-stalls
 * a hosted WebSocket and makes playback hitch.
 */
export const SNAPSHOT_EVERY_TICKS = 3;

const STILL: InputVector = { x: 0, y: 0 };

export const MSG = {
  INPUT: 'INPUT',
  MATCH_INIT: 'MATCH_INIT',
  SNAPSHOT: 'SNAPSHOT',
  MATCH_END: 'MATCH_END',
  ERROR: 'ERROR',
  PING: 'PING',
  PONG: 'PONG',
} as const;

export type Msg = (typeof MSG)[keyof typeof MSG];

export interface JoinOptions {
  supabaseRoomId: string;
  /** Hint only; the match server loads seed/map from the lobby row. */
  seed: number;
  mapId: string;
  camp: Camp;
  /** Supabase access JWT or visitor token. Prefer Client.auth.token; this field is a fallback. */
  accessToken?: string;
}

export interface InputPayload {
  sequence: number;
  /** Client tick this stick was sampled for. Informational; the server still holds last-input. */
  tick?: number;
  x: number;
  y: number;
}

export interface MatchInitPayload {
  protocolVersion: number;
  simulationVersion: string;
  contentHash: string;
  camp: Camp;
  seed: number;
  mapId: string;
  supabaseRoomId: string;
  players: number;
}

export interface SnapshotPayload {
  tick: number;
  /** Last input sequence applied into this snapshot for the receiving camp. */
  ack: number;
  /**
   * Simulation tick the `ack` belongs to (`state.tick`, next tick to process).
   * Sequence alone cannot identify which historical ticks used that input.
   */
  ackTick: number;
  state: MatchState;
  events: readonly SimulationEvent[];
  /** Held sticks at this tick so the client can replay prediction. */
  inputs: TickInputs;
}

/** Server ladder write outcome on MATCH_END (ADR-026). */
export type LadderPersistStatus = 'saved' | 'skipped' | 'failed';

export interface MatchEndPayload {
  result: MatchResult;
  /** Present when the match server finished the ladder write attempt. */
  ladder?: LadderPersistStatus;
}

export interface ErrorPayload {
  code: string;
  message: string;
}

export interface PingPayload {
  t: number;
}

export interface PongPayload {
  t: number;
  tick: number;
}

export function expectedSimulationVersion(): string {
  return SIMULATION_VERSION;
}

/**
 * A visitor plays online with a name only: no email, no Supabase session.
 * The bearer token is `visitor.<secret>`. The seat id on the lobby row is
 * derived from the secret, so seeing the id does not hand over the seat.
 */
export const VISITOR_TOKEN_PREFIX = 'visitor.';
/** Hashed with the secret into the visitor's seat id. */
export const VISITOR_ID_SALT = 'orbomb.visitor.v1:';
export const VISITOR_NAME_MAX = 20;

const VISITOR_NAME = /^[A-Za-z0-9 _-]{2,20}$/;
/** 32 random bytes, base64url without padding. */
const VISITOR_SECRET = /^[A-Za-z0-9_-]{43}$/;

/** Same letters as an account name, up to 20 characters. */
export function sanitizeVisitorName(value: string): string | null {
  const name = value.trim().replace(/\s+/g, ' ');
  return VISITOR_NAME.test(name) ? name : null;
}

export function visitorSecretFromToken(token: string): string | null {
  if (!token.startsWith(VISITOR_TOKEN_PREFIX)) return null;
  const secret = token.slice(VISITOR_TOKEN_PREFIX.length);
  return VISITOR_SECRET.test(secret) ? secret : null;
}

/**
 * Seat id from SHA-256(`VISITOR_ID_SALT` + secret): the first 16 bytes laid
 * out as a version-4 uuid, so it fits the lobby's uuid columns. Each side
 * hashes with its own crypto (`node:crypto`, `crypto.subtle`).
 */
export function visitorIdFromDigest(digest: Uint8Array): string {
  if (digest.length < 16) throw new Error('Visitor digest is too short.');
  // Copy: a Node Buffer's subarray/slice is a view onto the digest.
  const bytes = Uint8Array.from(digest.subarray(0, 16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function parseJoinOptions(value: unknown): JoinOptions | null {
  const raw = asRow(value);
  if (!raw) return null;
  const supabaseRoomId = asText(raw.supabaseRoomId ?? raw.supabase_room_id);
  const seed = asWhole(raw.seed);
  const mapId = asText(raw.mapId ?? raw.map_id);
  const camp = asCamp(raw.camp);
  if (!supabaseRoomId || seed === null || !mapId || !camp) return null;
  const accessToken = asText(raw.accessToken ?? raw.access_token) ?? undefined;
  return accessToken
    ? { supabaseRoomId, seed, mapId, camp, accessToken }
    : { supabaseRoomId, seed, mapId, camp };
}

export function parseInputPayload(value: unknown): InputPayload | null {
  const raw = asRow(value);
  if (!raw) return null;
  const sequence = asWhole(raw.sequence);
  const x = asAxis(raw.x);
  const y = asAxis(raw.y);
  if (sequence === null || x === null || y === null) return null;
  const parsedTick = raw.tick === undefined ? undefined : asWhole(raw.tick);
  if (parsedTick === null) return null;
  return parsedTick === undefined ? { sequence, x, y } : { sequence, tick: parsedTick, x, y };
}

export function parseMatchInit(value: unknown): MatchInitPayload | null {
  const raw = asRow(value);
  if (!raw) return null;
  const protocolVersion = asWhole(raw.protocolVersion);
  const simulationVersion = asText(raw.simulationVersion);
  const contentHash = asText(raw.contentHash);
  const camp = asCamp(raw.camp);
  const seed = asWhole(raw.seed);
  const mapId = asText(raw.mapId);
  const supabaseRoomId = asText(raw.supabaseRoomId);
  const players = asWhole(raw.players);
  if (
    protocolVersion === null ||
    !simulationVersion ||
    !contentHash ||
    !camp ||
    seed === null ||
    !mapId ||
    !supabaseRoomId ||
    players === null
  ) {
    return null;
  }
  return {
    protocolVersion,
    simulationVersion,
    contentHash,
    camp,
    seed,
    mapId,
    supabaseRoomId,
    players,
  };
}

export function parseSnapshot(value: unknown): SnapshotPayload | null {
  const raw = asRow(value);
  if (!raw) return null;
  const tick = asWhole(raw.tick);
  const ack = asWhole(raw.ack);
  if (tick === null || ack === null) return null;
  const ackTick = raw.ackTick === undefined ? tick : asWhole(raw.ackTick);
  if (ackTick === null) return null;
  const state = parseMatchState(raw.state);
  if (!state) return null;
  const events = Array.isArray(raw.events) ? (raw.events as SimulationEvent[]) : [];
  return {
    tick,
    ack,
    ackTick,
    state,
    events,
    inputs: parseTickInputs(raw.inputs),
  };
}

const MATCH_PHASES = new Set(['COUNTDOWN', 'REGULATION', 'OVERTIME', 'SUDDEN_DEATH', 'ENDED']);

/** Rejects truncated or non-finite wire state before the client applies it. */
function parseMatchState(value: unknown): MatchState | null {
  const raw = asRow(value);
  if (!raw) return null;
  const tick = asWhole(raw.tick);
  const seed = asWhole(raw.seed);
  const rng = asWhole(raw.rng);
  const nextExplosionId = asWhole(raw.nextExplosionId);
  const mapId = asText(raw.mapId);
  const simulationVersion = asText(raw.simulationVersion);
  const contentHash = asText(raw.contentHash);
  const balanceHash = asText(raw.balanceHash);
  if (
    tick === null ||
    seed === null ||
    rng === null ||
    nextExplosionId === null ||
    !mapId ||
    !simulationVersion ||
    !contentHash ||
    !balanceHash ||
    (raw.spawnRegion !== 0 && raw.spawnRegion !== 1) ||
    !Array.isArray(raw.players) ||
    raw.players.length !== 2 ||
    !Array.isArray(raw.goldens) ||
    !Array.isArray(raw.totems) ||
    raw.totems.length !== 2 ||
    !Array.isArray(raw.explosions) ||
    !Array.isArray(raw.bonuses) ||
    asWhole(raw.nextBonusId) === null ||
    !parseClockState(raw.clock) ||
    !parseTotemPair(raw.totems) ||
    !parseExplosions(raw.explosions) ||
    (raw.result !== null && raw.result !== undefined && !asRow(raw.result)) ||
    (raw.stats !== undefined && !asRow(raw.stats))
  ) {
    return null;
  }
  for (const player of raw.players) {
    if (!parseBodyPose(player)) return null;
  }
  for (const golden of raw.goldens) {
    if (!parseBodyPose(golden)) return null;
  }
  for (const bonus of raw.bonuses) {
    if (!parseBonus(bonus)) return null;
  }
  return raw as unknown as MatchState;
}

function parseClockState(value: unknown): boolean {
  const raw = asRow(value);
  if (!raw) return false;
  if (typeof raw.phase !== 'string' || !MATCH_PHASES.has(raw.phase)) return false;
  return (
    asWhole(raw.countdownEndTick) !== null &&
    asWhole(raw.regulationEndTick) !== null &&
    asWhole(raw.nextBonusTick) !== null
  );
}

function parseBonus(value: unknown): boolean {
  const raw = asRow(value);
  if (!raw) return false;
  return (
    asText(raw.id) !== null &&
    raw.kind === 'ONE_UP' &&
    asFinite(raw.x) !== null &&
    asFinite(raw.y) !== null &&
    asWhole(raw.spawnedAtTick) !== null &&
    asWhole(raw.expiresAtTick) !== null
  );
}

function parseTotemPair(totems: unknown[]): boolean {
  let orange = 0;
  let turquoise = 0;
  for (const item of totems) {
    const raw = asRow(item);
    if (!raw) return false;
    if (asWhole(raw.hp) === null) return false;
    if (raw.camp === 'ORANGE' && raw.id === 'totem-orange') orange += 1;
    else if (raw.camp === 'TURQUOISE' && raw.id === 'totem-turquoise') turquoise += 1;
    else return false;
  }
  return orange === 1 && turquoise === 1;
}

/** Fields the client reads for blast drawing; reject truncated crosses. */
function parseExplosions(items: unknown[]): boolean {
  for (const item of items) {
    const raw = asRow(item);
    if (!raw) return false;
    if (asWhole(raw.id) === null || asWhole(raw.startedAtTick) === null) return false;
    if (asWhole(raw.lethalUntilTick) === null || asWhole(raw.visualUntilTick) === null) {
      return false;
    }
    const cross = asRow(raw.cross);
    if (!cross || asWhole(cross.col) === null || asWhole(cross.row) === null) return false;
    if (!asRow(cross.horizontal) || !asRow(cross.vertical) || !asRow(cross.reach)) return false;
  }
  return true;
}

function parseBodyPose(value: unknown): boolean {
  const raw = asRow(value);
  if (!raw) return false;
  return (
    asFinite(raw.x) !== null &&
    asFinite(raw.y) !== null &&
    asFinite(raw.vx) !== null &&
    asFinite(raw.vy) !== null
  );
}

function parseTickInputs(value: unknown): TickInputs {
  const raw = asRow(value);
  if (!raw) return { ORANGE: STILL, TURQUOISE: STILL };
  return {
    ORANGE: parseInputVector(raw.ORANGE) ?? STILL,
    TURQUOISE: parseInputVector(raw.TURQUOISE) ?? STILL,
  };
}

function parseInputVector(value: unknown): InputVector | null {
  const raw = asRow(value);
  if (!raw) return null;
  const x = asAxis(raw.x);
  const y = asAxis(raw.y);
  if (x === null || y === null) return null;
  return { x, y };
}

const END_CAUSES = new Set([
  'TOTEM_DESTROYED',
  'LIVES_AFTER_OVERTIME',
  'SUDDEN_DEATH_TOTEM',
  'FORFEIT',
  // Legacy wire / ladder names still accepted when reading old payloads.
  'DOUBLE_DESTRUCTION',
  'SCORE_AFTER_OVERTIME',
  'SUDDEN_DEATH_POINT',
]);

export function parseMatchEnd(value: unknown): MatchEndPayload | null {
  const raw = asRow(value);
  if (!raw) return null;
  const result = asRow(raw.result);
  if (!result) return null;
  const winner = asCamp(result.winner);
  const cause = asText(result.cause);
  const tick = asWhole(result.tick);
  if (!winner || !cause || !END_CAUSES.has(cause) || tick === null) return null;
  const score = parseCampScores(result.score);
  const totemHp = parseCampScores(result.totemHp);
  if (!score || !totemHp) return null;
  const ladder =
    raw.ladder === 'saved' || raw.ladder === 'skipped' || raw.ladder === 'failed'
      ? raw.ladder
      : undefined;
  return {
    result: {
      ...(result as unknown as MatchResult),
      winner,
      cause: cause as MatchResult['cause'],
      tick,
      score,
      totemHp,
    },
    ...(ladder ? { ladder } : {}),
  };
}

function parseCampScores(value: unknown): Record<Camp, number> | null {
  const raw = asRow(value);
  if (!raw) return null;
  const orange = asFinite(raw.ORANGE);
  const turquoise = asFinite(raw.TURQUOISE);
  if (orange === null || turquoise === null) return null;
  return { ORANGE: orange, TURQUOISE: turquoise };
}

export function parseError(value: unknown): ErrorPayload | null {
  const raw = asRow(value);
  if (!raw) return null;
  const code = asText(raw.code) ?? 'ERROR';
  const message = asText(raw.message) ?? 'Something went wrong.';
  return { code, message };
}

export function parsePing(value: unknown): PingPayload | null {
  const raw = asRow(value);
  if (!raw) return null;
  const t = asFinite(raw.t);
  if (t === null) return null;
  return { t };
}

export function parsePong(value: unknown): PongPayload | null {
  const raw = asRow(value);
  if (!raw) return null;
  const t = asFinite(raw.t);
  const tick = asWhole(raw.tick);
  if (t === null || tick === null) return null;
  return { t, tick };
}

function asRow(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asText(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asWhole(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return value;
  if (typeof value === 'string' && /^(0|[1-9]\d*)$/.test(value)) return Number(value);
  return null;
}

function asFinite(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function asAxis(value: unknown): number | null {
  let n: number | null = null;
  if (typeof value === 'number' && Number.isFinite(value)) n = value;
  else if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) n = parsed;
  }
  if (n === null) return null;
  return Math.max(-1, Math.min(1, n));
}

function asCamp(value: unknown): Camp | null {
  return value === 'ORANGE' || value === 'TURQUOISE' ? value : null;
}
